import { Input, Stack, Table } from '@chakra-ui/react'
import { format } from 'date-fns'
import { RiImageAddFill } from 'react-icons/ri'
import { useParams } from 'react-router-dom'

import { Button } from '@app/components/ui/button'
import { Field } from '@app/components/ui/field'
import { NativeSelectField, NativeSelectRoot } from '@app/components/ui/native-select'
import useGetObject from '@app/hooks/useGetObject'
import ContributorsInput from '@app/next/forms/inline/contributorsInput'
import LabelsInput from '@app/next/forms/inline/labelsInput'
import RichTextInput from '@app/next/forms/inline/richTextInput'
import ViewerFieldWrapper from '@app/next/forms/inline/support/viewerFieldWrapper'
import InlineUpdaterRoot from '@app/next/forms/inlineUpdaterRoot'
import OwnerPopover from '@app/next/ownerPopover'
import SourceSummary from '@app/pages/entities/components/sourceSummary'
import DetailsTable from '@app/pages/shared/detailsTable'
import CardEntityStatus from '@app/shared/cards/entity/cardEntityStatus'
import ExistingEntityAttachmentUploadButton from '@app/shared/entities/existingEntityAttachmentUploadButton'
import EntityImages from '@app/shared/entity/entityImages'
import { ImpactEnum, ImportanceEnum } from '@graphql/types'

const EntityDetails = () => {
  const { nodeId } = useParams()
  const entity = useGetObject(nodeId, 'entity')
  const { workflowState, foreignState } = entity

  return (
    <Stack gap={4} px={2}>
      <DetailsTable>
        <Table.Body>
          <ViewerFieldWrapper value={entity.summary}>
            <Table.Row>
              <Table.Cell fontWeight="semibold">Description</Table.Cell>
              <Table.Cell>
                <RichTextInput domainObject={entity} name="summary" placeholder="Description..." />
              </Table.Cell>
            </Table.Row>
          </ViewerFieldWrapper>
          {entity.sourceSummary && (
            <Table.Row>
              <Table.Cell fontWeight="semibold">Source Summary</Table.Cell>
              <Table.Cell>
                <SourceSummary sourceSummary={entity.sourceSummary} />
              </Table.Cell>
            </Table.Row>
          )}
          <ViewerFieldWrapper value={entity.goalHypothesis}>
            <Table.Row>
              <Table.Cell fontWeight="semibold">Goal / Hypothesis</Table.Cell>
              <Table.Cell>
                <RichTextInput domainObject={entity} name="goalHypothesis" placeholder="Goal / Hypothesis..." />
              </Table.Cell>
            </Table.Row>
          </ViewerFieldWrapper>
          <ViewerFieldWrapper value={entity.results}>
            <Table.Row>
              <Table.Cell fontWeight="semibold">Results</Table.Cell>
              <Table.Cell>
                <RichTextInput domainObject={entity} name="results" placeholder="Results..." />
              </Table.Cell>
            </Table.Row>
          </ViewerFieldWrapper>
          <ViewerFieldWrapper value={entity.happenedAt}>
            <Table.Row>
              <Table.Cell fontWeight="semibold">When did the event occur</Table.Cell>
              <Table.Cell>
                <InlineUpdaterRoot
                  domainObject={entity}
                  onBeforeSend={(dateTime) => (dateTime ? new Date(dateTime).toISOString() : null)}
                >
                  <Input
                    name="happenedAt"
                    type="datetime-local"
                    defaultValue={format(
                      entity.happenedAt ? new Date(entity.happenedAt) : new Date(),
                      "yyyy-MM-dd'T'HH:mm"
                    )}
                  />
                </InlineUpdaterRoot>
              </Table.Cell>
            </Table.Row>
          </ViewerFieldWrapper>
          <ViewerFieldWrapper value={entity.dueDate}>
            <Table.Row>
              <Table.Cell fontWeight="semibold">Due Date</Table.Cell>
              <Table.Cell>
                <InlineUpdaterRoot
                  domainObject={entity}
                  onBeforeSend={(date) => (date ? new Date(date).toISOString() : null)}
                >
                  <Input name="dueDate" type="date" defaultValue={entity.dueDate} />
                </InlineUpdaterRoot>
              </Table.Cell>
            </Table.Row>
          </ViewerFieldWrapper>
          <ViewerFieldWrapper value={entity.importance}>
            <Table.Row>
              <Table.Cell fontWeight="semibold">Importance</Table.Cell>
              <Table.Cell>
                <NativeSelectRoot>
                  <InlineUpdaterRoot domainObject={entity}>
                    <NativeSelectField name="importance" placeholder="" defaultValue={entity?.importance}>
                      <option value={ImportanceEnum.Minor}>Minor</option>
                      <option value={ImportanceEnum.Medium}>Medium</option>
                      <option value={ImportanceEnum.Major}>Major</option>
                    </NativeSelectField>
                  </InlineUpdaterRoot>
                </NativeSelectRoot>
              </Table.Cell>
            </Table.Row>
          </ViewerFieldWrapper>
          <ViewerFieldWrapper value={entity.impact}>
            <Table.Row>
              <Table.Cell fontWeight="semibold">Impact</Table.Cell>
              <Table.Cell>
                <NativeSelectRoot>
                  <InlineUpdaterRoot domainObject={entity}>
                    <NativeSelectField name="impact" placeholder="" defaultValue={entity?.impact}>
                      <option value="">None</option>
                      <option value={ImpactEnum.Positive}>Positive Impact</option>
                      <option value={ImpactEnum.Negative}>Negative Impact</option>
                    </NativeSelectField>
                  </InlineUpdaterRoot>
                </NativeSelectRoot>
              </Table.Cell>
            </Table.Row>
          </ViewerFieldWrapper>
          <ViewerFieldWrapper value={entity.entityType}>
            <Table.Row>
              <Table.Cell fontWeight="semibold">Type</Table.Cell>
              <Table.Cell>
                <InlineUpdaterRoot domainObject={entity}>
                  <Input name="entityType" placeholder="chore, feature, fix, etc." />
                </InlineUpdaterRoot>
              </Table.Cell>
            </Table.Row>
          </ViewerFieldWrapper>
          <ViewerFieldWrapper value={entity.linkTitle}>
            <Table.Row>
              <Table.Cell fontWeight="semibold">URL Title</Table.Cell>
              <Table.Cell>
                <Field helperText="The title of the link to the demonstration">
                  <InlineUpdaterRoot domainObject={entity}>
                    <Input name="linkTitle" placeholder="Demonstration" />
                  </InlineUpdaterRoot>
                </Field>
              </Table.Cell>
            </Table.Row>
          </ViewerFieldWrapper>
          <ViewerFieldWrapper value={entity.linkUrl}>
            <Table.Row>
              <Table.Cell fontWeight="semibold">URL</Table.Cell>
              <Table.Cell>
                <Field helperText="The URL to the demonstration">
                  <InlineUpdaterRoot domainObject={entity}>
                    <Input name="linkUrl" placeholder="https://..." />
                  </InlineUpdaterRoot>
                </Field>
              </Table.Cell>
            </Table.Row>
          </ViewerFieldWrapper>
          <ViewerFieldWrapper value={entity.owner}>
            <Table.Row>
              <Table.Cell fontWeight="semibold">Owner</Table.Cell>
              <Table.Cell>
                <OwnerPopover object={entity} showName />
              </Table.Cell>
            </Table.Row>
          </ViewerFieldWrapper>
          <ViewerFieldWrapper value={entity.labels}>
            <Table.Row>
              <Table.Cell fontWeight="semibold">Labels</Table.Cell>
              <Table.Cell>
                <LabelsInput domainObject={entity} name="labels" />
              </Table.Cell>
            </Table.Row>
          </ViewerFieldWrapper>
          <ViewerFieldWrapper value={foreignState}>
            <Table.Row>
              <Table.Cell fontWeight="semibold">Status</Table.Cell>
              <Table.Cell>
                <CardEntityStatus status={foreignState} workflowState={workflowState} />
              </Table.Cell>
            </Table.Row>
          </ViewerFieldWrapper>
          <ViewerFieldWrapper value={entity.entityContributions}>
            <Table.Row>
              <Table.Cell fontWeight="semibold">Contributors</Table.Cell>
              <Table.Cell>
                <ContributorsInput domainObject={entity} name="entityContributions" />
              </Table.Cell>
            </Table.Row>
          </ViewerFieldWrapper>
        </Table.Body>
      </DetailsTable>
      <EntityImages entity={entity} scrollContainer="entity-form" />
      <ExistingEntityAttachmentUploadButton entityId={entity.id}>
        <Button aria-label="Upload Attachments">
          <RiImageAddFill />
          Upload image
        </Button>
      </ExistingEntityAttachmentUploadButton>
    </Stack>
  )
}

export default EntityDetails
